/**
 * SignUpForm - Sign up form with Dashboard3 styling
 *
 * Complete registration form with account info, location, and student fields.
 *
 * @example
 * ```tsx
 * <SignUpForm />
 * ```
 */

"use client";

import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { FiAlertCircle, FiPlus } from "react-icons/fi";
import { RoleSelector } from "@/components/forms";
import { CustomSelectMultiple } from "@/components/shared/formElements/CustomSelect";
import { LabeledInput } from "@/components/shared/formElements/LabeledInput";
import { LabeledTextarea } from "@/components/shared/formElements/LabeledTextarea";
import { Button, Heading, Separator, Text } from "@/components/ui";
import {
  useAuthSignUp,
  useLocationsGet,
  usePreventInitialInputFocus,
  useSignupEmailLookup,
  useTenantData,
  useWaiversGetPublic,
} from "@/hooks";
import { useThemeTokens } from "@/lib/theme/tokens";
import { PageTypePortal } from "@/lib/types";
import { createOpenApiConfiguration } from "@/lib/utils";
import { getSignUpErrorMessage } from "@/lib/utils/auth/error-messages";
import {
  EXISTING_MEMBER_SIGNUP_MESSAGE,
  EXISTING_STUDIO_SIGNIN_MESSAGE,
  EXISTING_STUDIO_SIGNUP_MESSAGE,
  SIGNUP_EMAIL_STATUS,
} from "@/lib/utils/auth/signup-email-status";
import { PHONE_MAX_LENGTH } from "@/lib/utils/phone/phone";
import { WaiversPoliciesApi } from "@/openapi/generated";
import { type SignUpFormSchema, signUpSchema } from "@/schemas";

/** Placeholder waiver has policyId 0 – do not send accept for it */
const PLACEHOLDER_POLICY_ID = 0;

import {
  PublicEntrance,
  PublicEntranceBanner,
} from "@/components/shared/motion";
import { appendCallbackUrl } from "@/lib/utils/classRegistrationRedirect";
import { ClassRegistrationAuthBanner } from "./ClassRegistrationAuthBanner";
import { FamilyContactFields } from "./FamilyContactFields";
import { SignUpFormWrapper } from "./SignUpFormWrapper";
import { SignUpSuccessView } from "./SignUpSuccessView";
import { StudentFields } from "./StudentFields";
import { WaiverScrollAccept } from "./WaiverScrollAccept";

export const SignUpForm = () => {
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [role, setRole] = useState<"selfenroll" | "parent">("parent");
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  usePreventInitialInputFocus(formRef);

  const { data: locations } = useLocationsGet();
  const { items: waivers = [] } = useWaiversGetPublic();
  const { tenantId } = useTenantData() ?? {};

  const router = useRouter();
  const searchParams = useSearchParams();
  const theme = useThemeTokens();
  const callbackUrl = searchParams.get("callbackUrl");

  const locationOptions = useMemo(() => {
    if (!locations?.items) return [];
    return locations.items
      .filter((loc) => loc.isActive)
      .map((loc) => ({
        value: String(loc.locationId),
        label: loc.name,
      }));
  }, [locations]);

  const form = useForm<SignUpFormSchema>({
    resolver: zodResolver(signUpSchema),
    mode: "onChange",
    defaultValues: useMemo(
      () => ({
        role,
        accountKind: SIGNUP_EMAIL_STATUS.new,
        locations: [],
        terms: false,
        familyContacts: [
          {
            firstName: "",
            lastName: "",
            email: "",
            cellPhone: "",
            addressLine1: "",
          },
        ],
        students: [
          {
            firstName: "",
            lastName: "",
            birthday: { day: "", month: "", year: "" },
            gender: "",
          },
        ],
      }),
      [],
    ),
  });

  const {
    register,
    handleSubmit,
    control,
    formState,
    setValue,
    watch,
    getValues,
  } = form;

  const { errors } = formState;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "students",
  });

  const {
    fields: familyContactFields,
    append: appendFamilyContact,
    remove: removeFamilyContact,
  } = useFieldArray({
    control,
    name: "familyContacts",
  });

  const locationsValue = watch("locations");
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const email = watch("email");
  const {
    status: emailStatus,
    isChecking: isCheckingEmail,
    user: existingUser,
  } = useSignupEmailLookup(email);
  const isExistingStudio = emailStatus === SIGNUP_EMAIL_STATUS.existingStudio;
  const isExistingMember = emailStatus === SIGNUP_EMAIL_STATUS.existingMember;
  const showPasswordFields = !isExistingStudio && !isExistingMember;

  useEffect(() => {
    if (locationOptions.length === 1 && locationsValue.length === 0) {
      setValue("locations", [locationOptions[0].value], {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [locationOptions, locationsValue, setValue]);

  // When role changes to "selfenroll", populate student fields with account info
  // Also update when firstName/lastName changes while role is "selfenroll"
  useEffect(() => {
    if (role === "selfenroll" && fields.length > 0) {
      if (firstName) {
        setValue(`students.0.firstName`, firstName, {
          shouldValidate: true,
        });
      }
      if (lastName) {
        setValue(`students.0.lastName`, lastName, {
          shouldValidate: true,
        });
      }
    }
  }, [role, firstName, lastName, fields.length, setValue]);

  useEffect(() => {
    const nextKind =
      emailStatus === "unknown" ? SIGNUP_EMAIL_STATUS.new : emailStatus;
    setValue("accountKind", nextKind, {
      shouldValidate: emailStatus !== "unknown",
    });

    if (
      emailStatus === SIGNUP_EMAIL_STATUS.existingStudio ||
      emailStatus === SIGNUP_EMAIL_STATUS.existingMember
    ) {
      setValue("password", "", { shouldDirty: false, shouldValidate: false });
      setValue("confirmPassword", "", {
        shouldDirty: false,
        shouldValidate: false,
      });
    }

    if (emailStatus === SIGNUP_EMAIL_STATUS.existingStudio && existingUser) {
      if (existingUser.firstName && !getValues("firstName")) {
        setValue("firstName", existingUser.firstName, { shouldDirty: false });
      }
      if (existingUser.lastName && !getValues("lastName")) {
        setValue("lastName", existingUser.lastName, { shouldDirty: false });
      }
    }
  }, [emailStatus, existingUser, getValues, setValue]);

  const {
    mutate,
    isPending,
    isSuccess,
    isError,
    isAutoLoginSuccess,
    autoLoginError,
    didSkipAutoLogin,
  } = useAuthSignUp({
    onRegisterSuccess: async (variables) => {
      if (!waivers.length || tenantId == null) return;
      const api = new WaiversPoliciesApi(createOpenApiConfiguration());
      for (const waiver of waivers) {
        if (waiver.policyId === PLACEHOLDER_POLICY_ID) continue;
        try {
          await api.apiPoliciesIdAcceptPost({
            id: Number(waiver.policyId),
            acceptPolicyRequest: {
              userEmail: variables.email,
              policyName: waiver.name,
              policyDisplayText: waiver.displayText ?? null,
            },
            xTenantID: Number(tenantId),
          });
        } catch {
          // Continue with other waivers; don't block success UI
        }
      }
    },
    onSuccess: () => {
      // Success is handled by showing SignUpSuccessView
      setError(null);
    },
    onError: (errorMessage) => {
      // Set inline error for signup errors
      // Auto-login errors are handled by showing login fallback view
      setError(getSignUpErrorMessage(errorMessage));
    },
  });

  const onSubmit = useCallback(
    (data: SignUpFormSchema) => {
      if (data.accountKind === SIGNUP_EMAIL_STATUS.existingMember) {
        return;
      }
      setHasSubmitted(true);
      setError(null);
      mutate(data);
    },
    [mutate],
  );

  const goToLogin = useCallback(() => {
    const loginPath = callbackUrl
      ? appendCallbackUrl(PageTypePortal.Login, callbackUrl)
      : PageTypePortal.Login;
    router.push(loginPath);
  }, [router, callbackUrl]);

  const goToForgot = useCallback(() => {
    router.push(PageTypePortal.ForgotPassword);
  }, [router]);

  const addFamilyContact = useCallback(() => {
    appendFamilyContact({
      firstName: "",
      lastName: "",
      email: "",
      cellPhone: "",
      addressLine1: "",
    });
  }, [appendFamilyContact]);

  const addStudent = useCallback(() => {
    append({
      firstName: "",
      lastName: "",
      birthday: {
        day: "",
        month: "",
        year: "",
      },
      gender: "",
    });
  }, [append]);

  const showSuccessView = useMemo(
    () => hasSubmitted && !isError && isSuccess && isAutoLoginSuccess,
    [hasSubmitted, isError, isSuccess, isAutoLoginSuccess],
  );

  // Show success view if signup and auto-login succeeded
  if (showSuccessView) {
    return <SignUpSuccessView />;
  }

  // Show fallback if signup succeeded but auto-login failed or was skipped
  if (
    hasSubmitted &&
    !isError &&
    isSuccess &&
    !isAutoLoginSuccess &&
    autoLoginError
  ) {
    return (
      <SignUpFormWrapper>
        <Flex
          direction="column"
          align="center"
          textAlign="center"
          gap={4}
          maxW="480px"
          mx="auto"
          py={{ base: 2, md: 4 }}
        >
          <Heading
            variant="primary"
            fontSize={{ base: "2xl", md: "32px" }}
            lineHeight="1.2"
          >
            {didSkipAutoLogin
              ? "You're almost done"
              : "Account created successfully! 🎉"}
          </Heading>
          <Text
            variant="tertiary"
            fontWeight="400"
            fontSize="md"
            lineHeight="1.6"
          >
            {didSkipAutoLogin
              ? EXISTING_STUDIO_SIGNIN_MESSAGE
              : "However, we couldn't automatically log you in. Please use the button below to go to the login page."}
          </Text>
          <Button
            h="44px"
            w="auto"
            px={6}
            fontSize="sm"
            variant="primary"
            fontWeight="500"
            onClick={goToLogin}
            mt={1}
          >
            Go to Login Page
          </Button>
        </Flex>
      </SignUpFormWrapper>
    );
  }

  return (
    <SignUpFormWrapper>
      <ClassRegistrationAuthBanner mode="sign-up" />
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <PublicEntranceBanner show={!!error}>
          <Box
            borderRadius="8px"
            borderWidth="1px"
            borderColor={theme.accent.primaryBorder}
            bg={theme.accent.mutedBg}
            color={theme.accent.primary}
            p={4}
            flexDirection="column"
            alignItems="flex-start"
            mb={4}
          >
            <Flex align="center" gap={2} w="100%">
              <Box flexShrink={0} h="20px">
                <Icon
                  as={FiAlertCircle}
                  w="20px"
                  h="20px"
                  color={theme.accent.primary}
                />
              </Box>
              <Text fontWeight="600" fontSize="sm">
                Registration failed
              </Text>
            </Flex>
            <Flex align="flex-start" gap={2} w="100%" mt={2}>
              <Box flexShrink={0} w="20px" />
              <Text fontSize="sm">{error}</Text>
            </Flex>
          </Box>
        </PublicEntranceBanner>

        <PublicEntrance variant="group" delay={0.1}>
          <Box>
            <Text
              variant="tertiary"
              ms="4px"
              mb="10px"
              fontSize="md"
              fontWeight="400"
            >
              Already have an account with us?
            </Text>
            <Flex gap="15px" flexDirection={"row"}>
              <Button
                h="44px"
                fontSize="sm"
                variant="primary"
                fontWeight="500"
                onClick={goToLogin}
                w={{ base: "100%", md: "150px" }}
              >
                Sign In
              </Button>
              <Button
                h="44px"
                fontSize="sm"
                fontWeight="500"
                variant="outline"
                onClick={goToForgot}
                w={{ base: "100%", md: "150px" }}
              >
                Forgot password
              </Button>
            </Flex>
          </Box>

          <Separator my="20px" />
        </PublicEntrance>

        <PublicEntrance variant="group" delay={0.18}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px">
            <LabeledInput
              name="email"
              label="Email"
              errors={errors}
              register={register}
              type="email"
              placeholder="mail@example.com"
              required
            />
            <LabeledInput
              name="confirmEmail"
              label="Enter Email Again"
              errors={errors}
              register={register}
              type="email"
              placeholder="mail@example.com"
              required
            />
            {isCheckingEmail ? (
              <Text
                gridColumn={{ base: "1", md: "1 / span 2" }}
                fontSize="sm"
                color={theme.text.tertiary}
                data-testid="signup-email-checking"
              >
                Checking this email…
              </Text>
            ) : null}
            {isExistingStudio ? (
              <Box
                gridColumn={{ base: "1", md: "1 / span 2" }}
                borderRadius="8px"
                borderWidth="1px"
                borderColor={theme.accent.primaryBorder}
                bg={theme.accent.mutedBg}
                color={theme.accent.primary}
                p={4}
                data-testid="signup-existing-studio-notice"
              >
                <Text fontSize="sm" fontWeight="500">
                  {EXISTING_STUDIO_SIGNUP_MESSAGE}
                </Text>
              </Box>
            ) : null}
            {isExistingMember ? (
              <Box
                gridColumn={{ base: "1", md: "1 / span 2" }}
                borderRadius="8px"
                borderWidth="1px"
                borderColor={theme.accent.primaryBorder}
                bg={theme.accent.mutedBg}
                color={theme.accent.primary}
                p={4}
                data-testid="signup-existing-member-notice"
              >
                <Text fontSize="sm" fontWeight="500">
                  {EXISTING_MEMBER_SIGNUP_MESSAGE}
                </Text>
              </Box>
            ) : null}
            {showPasswordFields ? (
              <>
                <LabeledInput
                  name="password"
                  label="Password"
                  errors={errors}
                  register={register}
                  type="password"
                  placeholder="••••••"
                  required
                />
                <LabeledInput
                  name="confirmPassword"
                  label="Enter Password Again"
                  errors={errors}
                  register={register}
                  type="password"
                  placeholder="••••••"
                  required
                />
              </>
            ) : null}

            <LabeledInput
              name="firstName"
              label="First Name"
              errors={errors}
              register={register}
              placeholder="First Name"
              required
            />
            <LabeledInput
              name="lastName"
              label="Last Name"
              errors={errors}
              register={register}
              placeholder="Last Name"
              required
            />
            <LabeledInput
              name="cellPhone"
              label="Phone"
              errors={errors}
              register={register}
              type="text"
              placeholder="Phone"
              maxLength={PHONE_MAX_LENGTH}
              required
            />

            <Box gridColumn={{ base: "1", md: "1 / span 2" }}>
              <LabeledInput
                name="address"
                label="Street Address"
                errors={errors}
                register={register}
                placeholder="Enter your street address"
                required
              />
            </Box>
            <LabeledInput
              name="city"
              label="City"
              errors={errors}
              register={register}
              placeholder="City"
              required
            />
            <LabeledInput
              name="state"
              label="State"
              errors={errors}
              register={register}
              placeholder="State"
              required
            />
            <LabeledInput
              name="zip"
              label="Zip Code"
              errors={errors}
              register={register}
              placeholder="Zip code"
              required
            />

            <Box gridColumn={{ base: "1", md: "1 / span 2" }}>
              <LabeledTextarea
                name="heardAboutUs"
                label="How did you hear about us?"
                errors={errors}
                register={register}
                placeholder="Enter your message"
                rows={3}
              />
            </Box>

            <Separator
              m="20px 0 10px"
              gridColumn={{ base: "1", md: "1 / span 2" }}
            />
            <Text variant="primary" fontSize="2xl" fontWeight="700">
              Taking Classes
            </Text>
            <Box gridColumn={{ base: "1", md: "1 / span 2" }}>
              {locationOptions.length > 1 ? (
                <Controller
                  control={control}
                  name="locations"
                  render={({ field: locationsField }) => (
                    <CustomSelectMultiple
                      label="Location(s)"
                      placeholder="Select location(s)"
                      options={locationOptions.map((loc) => ({
                        value: loc.value,
                        label: loc.label,
                      }))}
                      selected={locationsField.value}
                      onChange={(selectedValues) => {
                        locationsField.onChange(selectedValues);
                      }}
                      formControlProps={{
                        isInvalid: !!errors.locations,
                      }}
                      required
                      error={errors.locations}
                      testId="locations-select"
                    />
                  )}
                />
              ) : locationOptions.length === 1 ? (
                <Text variant="primary" fontSize="md" mb="8px">
                  Location:{" "}
                  <Text as="span" fontWeight="700" variant="primary">
                    {locationOptions[0].label}
                  </Text>
                </Text>
              ) : null}

              {errors.locations && locationOptions.length <= 1 && (
                <Text
                  color={theme.accent.primary}
                  fontSize="xs"
                  fontWeight="500"
                  mt={1.5}
                >
                  {errors.locations.message}
                </Text>
              )}
            </Box>

            <Box
              gridColumn={{ base: "1", md: "1 / span 2" }}
              borderTopWidth="1px"
              borderBottomWidth="1px"
              borderColor={theme.borderGlass.default}
              py={{ base: 4, md: 5 }}
            >
              <Text variant="primary" fontSize="2xl" fontWeight="700" mb="8px">
                Family and emergency contact
              </Text>
              <Text variant="tertiary" fontSize="sm" mb="20px">
                This is who we call in an emergency, so it needs to be someone
                we can reach. One person is required.
              </Text>
              {familyContactFields.map((field, index) => (
                <FamilyContactFields
                  key={field.id}
                  index={index}
                  field={field}
                  remove={removeFamilyContact}
                  errors={errors}
                  register={register}
                  fieldsLength={familyContactFields.length}
                />
              ))}
              <Button
                type="button"
                mb="8px"
                variant="outline"
                onClick={addFamilyContact}
                leftIcon={<FiPlus />}
              >
                Add another family contact
              </Button>
            </Box>

            <Box gridColumn={{ base: "1", md: "1 / span 2" }}>
              <WaiverScrollAccept
                isChecked={watch("terms") || false}
                onCheckedChange={(checked) =>
                  setValue("terms", checked, { shouldValidate: true })
                }
                error={errors.terms?.message}
              />
            </Box>
          </SimpleGrid>

          <Separator my="40px" />
        </PublicEntrance>

        <PublicEntrance variant="group" delay={0.26}>
          <RoleSelector
            role={role}
            setRole={(r) => setRole(r as "parent" | "selfenroll")}
            options={[
              { label: "Add a student", value: "parent" },
              { label: "I am the student", value: "selfenroll" },
            ]}
          />

          {fields.map((field, index) => (
            <StudentFields
              index={index}
              field={field}
              key={field.id}
              remove={remove}
              errors={errors}
              register={register}
              control={control}
              fieldsLength={fields.length}
            />
          ))}

          {role === "parent" && (
            <Button
              mb="20px"
              variant="outline"
              onClick={addStudent}
              leftIcon={<FiPlus />}
            >
              Add another student
            </Button>
          )}

          <Button
            h="44px"
            w="100%"
            type="submit"
            fontSize="sm"
            variant="primary"
            fontWeight="500"
            isLoading={isPending}
            isDisabled={isPending || isCheckingEmail || isExistingMember}
          >
            Create Account
          </Button>
        </PublicEntrance>
      </form>
    </SignUpFormWrapper>
  );
};
